import { type QueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { type ChallengeConfigsApi } from '../../challenge-configs-api.ts';
import { type CoursesApi } from '../../courses-api.ts';
import type { PerformanceDashboard, PerformanceDashboardParams } from '../performance-api.ts';
import { buildPerformanceDashboard } from './build-dashboard.ts';
import {
  challengeConfigsDictionaryQueryOptions,
  coursesDictionaryQueryOptions,
} from './dictionaries-query-options.ts';
import { fetchAllPages } from './fetch-all-pages.ts';
import { getPeriodRange } from './period-range.ts';
import { SessionsServiceApi } from './sessions-service-api.ts';

// The session service is called directly and without auth until the aggregation service exists.
const SESSIONS_SERVICE_API_URL = 'https://dev-gs.moneyballgolf.pro/api';
const SESSIONS_PAGE_LIMIT = 100;

export class ClientPerformanceAggregation {
  private sessionsService = new SessionsServiceApi(
    axios.create({ baseURL: SESSIONS_SERVICE_API_URL })
  );

  constructor(
    private courses: CoursesApi,
    private challengeConfigs: ChallengeConfigsApi,
    private queryClient: QueryClient
  ) {}

  public async getDashboard(params: PerformanceDashboardParams): Promise<PerformanceDashboard> {
    const now = new Date();
    const range = getPeriodRange(params.period, params.time_zone, now);

    const [sessions, courses, challengeConfigs, regionCourses] = await Promise.all([
      this.getSessions(range.from, range.to),
      this.queryClient.ensureQueryData(coursesDictionaryQueryOptions(this.courses)),
      this.queryClient.ensureQueryData(
        challengeConfigsDictionaryQueryOptions(this.challengeConfigs)
      ),
      params.region_id === undefined
        ? undefined
        : this.queryClient.ensureQueryData(
            coursesDictionaryQueryOptions(this.courses, params.region_id)
          ),
    ]);

    return buildPerformanceDashboard({
      params,
      range,
      now,
      sessions,
      courses,
      challengeConfigs,
      regionCourses,
    });
  }

  private async getSessions(from: Date, to: Date) {
    const pages = await fetchAllPages(
      (page) =>
        this.sessionsService.getSessions({
          page,
          limit: SESSIONS_PAGE_LIMIT,
          date_from: from.toISOString(),
          date_to: to.toISOString(),
        }),
      (lastPage, page) => lastPage.sessions.length > 0 && page * lastPage.limit < lastPage.total
    );
    return pages.flatMap((page) => page.sessions);
  }
}
